import { Component, OnInit, computed, effect, inject, input, output } from '@angular/core';
import { DoodProductModel } from '@core/models/product.model';
import { ProductKeys } from '@config/keys/product.keys';
import { ProductService } from '@core/services/product/product.service';
import {
  ContentBuilderColorValue,
  IContentBuilderFieldColor,
} from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { ProductPushV2AtomHoverColors } from '@shared/interfaces/product-hover-colors.interface';
import { ProductTrackerService } from '@core/services/app/product-tracker.service';
import { fromContentBuilderColor } from '@shared/utils/content-builder-color/content-builder-color';
import { ProductColorOverrides } from '@shared/interfaces/product-category-color-overrides.interface';
import { InterfaceToInputs } from '@shared/utils/types/interface-to-inputs';
import { PriceAtomComponent } from '../price-atom/price-atom.component';
import { TagAtomComponent } from '../tag-atom/tag-atom.component';
import { VisibleLabelsPipe } from '@shared/pipes/visible-product-labels/visible-product-labels.pipe';
import { DiscountPercentPipe } from '@core/pipes/discount-percent.pipe';
import { CartStoreSelector } from '@common/selectors/cart.selector';
import { CartItemKeys } from '@config/keys/cart.keys';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';

@Component({
  selector: 'app-product-card-atom',
  templateUrl: './product-card-atom.component.html',
  styleUrls: ['./product-card-atom.component.scss'],
  imports: [
    PriceAtomComponent,
    TagAtomComponent,
    VisibleLabelsPipe,
    DiscountPercentPipe,
    ContentBuilderColorPipe,
  ],
  host: {
    '[style.--hover-bg-color]': 'hoverBackgroundColorValue',
    '[style.--hover-name-color]': 'hoverNameColorValue',
    '[style.--hover-description-color]': 'hoverDescriptionColorValue',
    '[style.--hover-price-color]': 'hoverPriceColorValue',
    '[style.--product-name-color]': 'productNameColorValue',
    '[style.--product-description-color]': 'productDescriptionColorValue',
    '[style.--product-price-color]': 'productPriceColorValue',
    '[style.--product-image-placeholder-color]': 'productImagePlaceholderColorValue',
    '[style.--product-border-color]': 'productBorderColorValue',
    '[style.--product-background-color]': 'productBackgroundColorValue',
  },
})
export class ProductCardAtomComponent
  implements OnInit, InterfaceToInputs<ProductColorOverrides, { required: false }>
{
  protected readonly defaultProductNameColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultProductDescriptionColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultProductPriceColor: IContentBuilderFieldColor = {
    value: 'neutral-900',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultProductImagePlaceholderColor: IContentBuilderFieldColor = {
    value: 'neutral-50',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultProductBorderColor: IContentBuilderFieldColor = {
    value: 'neutral-100',
    type: ColorFieldTypesValues.Palette,
  };

  protected readonly defaultProductBackgroundColor: IContentBuilderFieldColor = {
    value: '#FFFFFF',
    type: ColorFieldTypesValues.Hex,
  };

  protected readonly defaultHoverBackgroundColor: IContentBuilderFieldColor = {
    value: 'neutral-50',
    type: ColorFieldTypesValues.Palette,
  };
  private readonly productService = inject(ProductService);
  private readonly productTrackerService = inject(ProductTrackerService);
  private readonly cartSelector = inject(CartStoreSelector);

  readonly displayProductLabels = input<{
    displayLabels: boolean;
  }>({
    displayLabels: true,
  });

  readonly quantityInCart = computed(() => {
    const productId = this.product()[ProductKeys.Id];
    if (!productId) {
      return 0;
    }
    const productIdAsString = String(productId);

    return (
      this.cartSelector
        .selectActive()
        ?.products?.filter(item => String(item[CartItemKeys.ItemId]) === productIdAsString)
        .reduce((acc, item) => acc + (item[CartItemKeys.Quantity] ?? 0), 0) ?? 0
    );
  });

  constructor() {
    effect(() => {
      const product = this.product();
      const displayContext = this.displayContext();
      if (product && displayContext !== undefined) {
        this.productTrackerService.trackProductDisplayed(product, displayContext);
      }
    });
  }

  displayContext = input('');
  product = input.required<DoodProductModel>();
  isOrderingAllowed = input(true);
  cardHeight = input<'dynamic' | 'small'>('dynamic');
  productNameColor = input<ContentBuilderColorValue | undefined>(this.defaultProductNameColor);
  productDescriptionColor = input<ContentBuilderColorValue | undefined>(
    this.defaultProductDescriptionColor,
  );
  productPriceColor = input<ContentBuilderColorValue | undefined>(this.defaultProductPriceColor);
  productImagePlaceholderColor = input<ContentBuilderColorValue | undefined>(
    this.defaultProductImagePlaceholderColor,
  );
  productBorderColor = input<ContentBuilderColorValue | undefined>();
  productBackgroundColor = input<ContentBuilderColorValue | undefined>(
    this.defaultProductBackgroundColor,
  );
  productHoverColors = input<ProductPushV2AtomHoverColors>();
  productClick = output<DoodProductModel>();

  protected get productNameColorValue(): string {
    return fromContentBuilderColor(this.productNameColor(), this.defaultProductNameColor);
  }

  protected get productDescriptionColorValue(): string {
    return fromContentBuilderColor(
      this.productDescriptionColor(),
      this.defaultProductDescriptionColor,
    );
  }

  protected get productPriceColorValue(): string {
    return fromContentBuilderColor(this.productPriceColor(), this.defaultProductPriceColor);
  }

  protected get productImagePlaceholderColorValue(): string {
    return fromContentBuilderColor(
      this.productImagePlaceholderColor(),
      this.defaultProductImagePlaceholderColor,
    );
  }

  protected get productBorderColorValue(): string {
    return fromContentBuilderColor(this.productBorderColor(), this.defaultProductBorderColor);
  }

  protected get productBackgroundColorValue(): string {
    return fromContentBuilderColor(
      this.productBackgroundColor(),
      this.defaultProductBackgroundColor,
    );
  }

  protected get hoverBackgroundColorValue(): string {
    return fromContentBuilderColor(
      this.productHoverColors()?.backgroundColor,
      this.defaultHoverBackgroundColor,
    );
  }

  protected get hoverNameColorValue(): string {
    return fromContentBuilderColor(
      this.productHoverColors()?.nameColor,
      this.productNameColor() || this.defaultProductNameColor,
    );
  }

  protected get hoverDescriptionColorValue(): string {
    return fromContentBuilderColor(
      this.productHoverColors()?.descriptionColor,
      this.productDescriptionColor() || this.defaultProductDescriptionColor,
    );
  }

  protected get hoverPriceColorValue(): string {
    return fromContentBuilderColor(
      this.productHoverColors()?.priceColor,
      this.productPriceColor() || this.defaultProductPriceColor,
    );
  }

  ngOnInit(): void {
    this.productTrackerService.trackProductDisplayed(this.product(), this.displayContext());
  }

  isAvailable(product: DoodProductModel): boolean {
    return this.productService.isAvailable(product);
  }

  get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  getImageContainerClass(): string {
    const base = 'relative shrink-0 overflow-hidden rounded-t-lg bg-neutral-50';

    if (this.cardHeight() === 'small') {
      return `${base} mx-auto aspect-square h-auto w-full max-h-36`;
    }

    return `${base} aspect-square w-full`;
  }

  onProductClick(product: DoodProductModel): void {
    if (!this.isAvailable(product)) {
      return;
    }
    this.productClick.emit(product);
  }
}
